/**
 * EditHistoryTimeline
 *
 * Vertical timeline of all past ProductEditRequest entries for a product.
 * Used in admin (and seller) detail views to show approval/rejection history.
 */

import React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

export interface EditRequestSummary {
  _id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  createdAt: string | Date;
  approvedAt?: string | Date;
  rejectedAt?: string | Date;
  cancelledAt?: string | Date;
  approvalNote?: string;
  rejectionReason?: string;
  approvedBy?: { name?: string };
  rejectedBy?: { name?: string };
  createdBy?: { name?: string };
}

const formatDate = (d: string | Date | undefined): string => {
  if (!d) return '—';
  try {
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(d));
  } catch {
    return '—';
  }
};

const STATUS_CONFIG: Record<EditRequestSummary['status'], { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  PENDING: { label: 'در انتظار بررسی', color: 'var(--color-warning-dark)', bg: 'var(--color-warning-bg)', icon: <PendingActionsIcon style={{ fontSize: 18 }} /> },
  APPROVED: { label: 'تایید شده', color: 'var(--color-success-dark)', bg: 'var(--color-success-bg)', icon: <CheckCircleIcon style={{ fontSize: 18 }} /> },
  REJECTED: { label: 'رد شده', color: 'var(--color-error-dark)', bg: 'var(--color-error-bg)', icon: <CancelIcon style={{ fontSize: 18 }} /> },
  CANCELLED: { label: 'لغو شده', color: 'var(--color-text-secondary)', bg: 'var(--color-bg-default)', icon: <RemoveCircleOutlineIcon style={{ fontSize: 18 }} /> },
};

export const EditHistoryTimeline: React.FC<{ history: EditRequestSummary[] }> = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          background: 'var(--color-bg-default)',
          borderRadius: 8,
          fontSize: 13,
        }}
      >
        تاریخچه‌ای برای این محصول ثبت نشده است
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingInlineStart: 28 }}>
      <div
        style={{
          position: 'absolute',
          insetInlineStart: 12,
          top: 6,
          bottom: 6,
          width: 2,
          background: 'var(--color-border)',
        }}
      />
      {history.map((req, idx) => {
        const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.PENDING;
        const decisionDate =
          req.status === 'APPROVED'
            ? req.approvedAt
            : req.status === 'REJECTED'
            ? req.rejectedAt
            : req.status === 'CANCELLED'
            ? req.cancelledAt
            : undefined;
        const decider =
          req.status === 'APPROVED'
            ? req.approvedBy?.name
            : req.status === 'REJECTED'
            ? req.rejectedBy?.name
            : undefined;

        return (
          <div key={req._id || idx} style={{ position: 'relative', marginBottom: 14 }}>
            <div
              style={{
                position: 'absolute',
                insetInlineStart: -22,
                top: 4,
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: cfg.bg,
                color: cfg.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `2px solid ${cfg.color}`,
              }}
            >
              {cfg.icon}
            </div>
            <div
              style={{
                background: 'var(--color-bg-paper)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                padding: '10px 12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ color: cfg.color, fontWeight: 700, fontSize: 13 }}>{cfg.label}</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-disabled)' }}>کد: {req._id?.slice(-6)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
                <div>
                  <strong>درخواست:</strong> {formatDate(req.createdAt)}
                  {req.createdBy?.name && <> &mdash; توسط {req.createdBy.name}</>}
                </div>
                {decisionDate && (
                  <div>
                    <strong>{req.status === 'APPROVED' ? 'تایید' : req.status === 'REJECTED' ? 'رد' : 'لغو'}:</strong>{' '}
                    {formatDate(decisionDate)}
                    {decider && <> &mdash; توسط {decider}</>}
                  </div>
                )}
                {req.approvalNote && (
                  <div style={{ marginTop: 6, padding: 8, background: 'var(--color-success-bg)', borderRadius: 6, color: 'var(--color-success-dark)' }}>
                    <strong>یادداشت تایید:</strong> {req.approvalNote}
                  </div>
                )}
                {req.rejectionReason && (
                  <div style={{ marginTop: 6, padding: 8, background: 'var(--color-error-bg)', borderRadius: 6, color: 'var(--color-error-dark)' }}>
                    <strong>دلیل رد:</strong> {req.rejectionReason}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EditHistoryTimeline;
